/**
 * FairwayBid – Beschaffungsperioden
 *
 * Golfanlagen beschaffen nicht gleichmäßig übers Jahr. Es gibt zwei Fenster:
 *
 *   Hauptperiode   1. September – 31. Januar  (Planung und Budget fürs Folgejahr)
 *   Zweite Periode 1. März – 30. April        (kurz vor Saisonstart)
 *
 * Dieses Modul beantwortet tagesaktuell: Wo stehen wir gerade? Daraus baut die
 * Startseite ihre Botschaft – für Golfanlagen und für Händler jeweils anders.
 *
 * Anpassen: PERIODS unten. Monate sind 1-basiert (1 = Januar).
 */

export const PERIODS = [
  { key: 'haupt', label: 'Hauptperiode', startMonth: 9, startDay: 1, endMonth: 1, endDay: 31 },
  {
    key: 'fruehjahr',
    label: 'Frühjahrsperiode',
    startMonth: 3,
    startDay: 1,
    endMonth: 4,
    endDay: 30,
  },
];

// So viele Tage vor Beginn wird bereits auf die kommende Periode hingewiesen.
export const LEAD_DAYS = 30;
// Ab so vielen Resttagen wird es dringlich.
export const ENDING_SOON_DAYS = 30;

/*
  Ab hier wird in Wochen gerechnet. Die Hauptperiode dauert fünf Monate –
  "noch 143 Tage" ist eine Zahl, die niemand in einen Kalender übersetzt,
  "noch 20 Wochen" schon. Unterhalb dieser Grenze bleiben es Tage: Wer in der
  letzten Phase steht, plant nicht mehr in Wochen.
*/
export const WOCHEN_AB_TAGEN = 30;

const wochen = tage => Math.max(1, Math.round(tage / 7));

/** "20 Wochen", "9 Tage", "1 Woche" – je nachdem, was hier hilft. */
export const formatDauer = tage => {
  if (tage == null) return '';
  if (tage <= 0) return 'heute';
  if (tage < WOCHEN_AB_TAGEN) return tage === 1 ? '1 Tag' : `${tage} Tage`;
  const w = wochen(tage);
  return w === 1 ? '1 Woche' : `${w} Wochen`;
};

/**
 * Dieselbe Angabe im Dativ, für Sätze wie "In drei Wochen beginnt ...".
 * "In 14 Tage" liest sich wie ein Tippfehler und beschädigt den ganzen Satz.
 */
export const formatDauerDativ = tage => {
  if (tage == null) return '';
  if (tage <= 0) return 'wenigen Stunden';
  if (tage === 1) return 'einem Tag';
  if (tage < WOCHEN_AB_TAGEN) return `${tage} Tagen`;
  const w = wochen(tage);
  return w === 1 ? 'einer Woche' : `${w} Wochen`;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const startOfDay = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const daysBetween = (a, b) => Math.round((startOfDay(b) - startOfDay(a)) / MS_PER_DAY);

/**
 * Konkrete Start- und Endtermine einer Periode für ein Bezugsjahr.
 * Perioden dürfen über den Jahreswechsel laufen (Sep–Jan).
 */
const occurrence = (period, year) => {
  const start = new Date(year, period.startMonth - 1, period.startDay);
  const crossesYear = period.endMonth < period.startMonth;
  const end = new Date(
    crossesYear ? year + 1 : year,
    period.endMonth - 1,
    period.endDay,
    23,
    59,
    59
  );
  return { period, start, end };
};

/**
 * Alle Vorkommen im Umfeld des Stichtags – Vorjahr, laufendes Jahr, Folgejahr.
 *
 * "keys" schraenkt auf die Perioden ein, die fuer eine bestimmte Golfanlage
 * gelten. Wer nur im Herbst beschafft, soll im Maerz nicht an ein Fenster
 * erinnert werden, das ihn nichts angeht.
 */
const allOccurrences = (year, keys = null) => {
  const perioden = keys ? PERIODS.filter(p => keys.includes(p.key)) : PERIODS;
  return [year - 1, year, year + 1]
    .flatMap(y => perioden.map(p => occurrence(p, y)))
    .sort((a, b) => a.start - b.start);
};

/**
 * Wo stehen wir heute?
 *
 * @param {Date} now Stichtag
 * @returns {Object} Status:
 *   status  'laufend' | 'beginnt-bald' | 'zwischen'
 *   label   Name der betroffenen Periode
 *   start / end  Termine der betroffenen Periode
 *   daysLeft     nur bei 'laufend': verbleibende Tage
 *   daysUntil    bei 'beginnt-bald' und 'zwischen': Tage bis zum Beginn
 *   endingSoon   bei 'laufend': true, wenn es knapp wird
 *   progress     bei 'laufend': 0..1, wie weit die Periode fortgeschritten ist
 */
export const getSeasonStatus = (now = new Date(), keys = null) => {
  const occs = allOccurrences(now.getFullYear(), keys);
  if (occs.length === 0) {
    return { status: 'ohne-fenster', key: null, label: null, start: null, end: null };
  }

  const current = occs.find(o => now >= o.start && now <= o.end);
  if (current) {
    const daysLeft = daysBetween(now, current.end);
    const total = daysBetween(current.start, current.end) || 1;
    return {
      status: 'laufend',
      key: current.period.key,
      label: current.period.label,
      start: current.start,
      end: current.end,
      daysLeft,
      restLabel: formatDauer(daysLeft),
      weeksTotal: wochen(total),
      endingSoon: daysLeft <= ENDING_SOON_DAYS,
      progress: Math.min(1, Math.max(0, daysBetween(current.start, now) / total)),
    };
  }

  const next = occs.find(o => o.start > now);
  const daysUntil = next ? daysBetween(now, next.start) : null;
  return {
    status: daysUntil !== null && daysUntil <= LEAD_DAYS ? 'beginnt-bald' : 'zwischen',
    key: next?.period.key || null,
    label: next?.period.label || null,
    start: next?.start || null,
    end: next?.end || null,
    daysUntil,
    restLabel: formatDauer(daysUntil),
    restLabelDativ: formatDauerDativ(daysUntil),
    weeksTotal: next ? wochen(daysBetween(next.start, next.end) || 1) : null,
  };
};

const dateLabel = d => (d ? d.toLocaleDateString('de-DE', { day: 'numeric', month: 'long' }) : '');

/**
 * Das Fenster selbst, in Wochen: "1. September bis 31. Januar · 22 Wochen".
 * Steht im Banner unter der Überschrift - wer neu ist, sieht damit sofort,
 * worüber wir reden, und muss nicht aus zwei Daten die Länge ausrechnen.
 */
export const fensterLabel = s =>
  s?.start && s?.end
    ? `${dateLabel(s.start)} bis ${dateLabel(s.end)} · ${s.weeksTotal} Wochen`
    : '';

/**
 * Botschaft für die Startseite, je nach Rolle und Periodenstand.
 *
 * @param {string} audience 'customer' (Golfanlage) oder 'provider' (Händler)
 * @param {Date} now Stichtag
 * @returns {{headline: string, body: string, ctaLabel: string, ctaTo: string, status: Object}}
 */
export const getSeasonMessage = (audience, now = new Date(), keys = null) => {
  const s = getSeasonStatus(now, keys);
  const isClub = audience !== 'provider';

  // Eine Anlage, die laufend beschafft, hat kein Fenster - dann gibt es auch
  // nichts zu erinnern. Statt einer erfundenen Frist steht dort der Hinweis,
  // dass Anfragen jederzeit gehen.
  if (s.status === 'ohne-fenster') {
    return {
      headline: 'Sie beschaffen laufend',
      body:
        'Sie haben angegeben, dass Sie über das Jahr verteilt einkaufen. Wir erinnern Sie deshalb nicht an Beschaffungsfenster. Eine Anfrage können Sie jederzeit einstellen.',
      ctaLabel: isClub ? 'Anfrage einstellen' : 'Offene Anfragen ansehen',
      ctaTo: isClub ? '/l/new' : '/s',
      status: s,
    };
  }

  if (s.status === 'laufend' && isClub) {
    return s.endingSoon
      ? {
          headline: `Noch ${s.restLabel} in der ${s.label}`,
          body: `Die ${s.label} endet am ${dateLabel(
            s.end
          )}. Wer jetzt ausschreibt, bekommt die Angebote noch innerhalb der Periode auf den Tisch. Danach wird es eng — Händler planen ihre Kapazitäten für die Saison durch.`,
          ctaLabel: 'Anfrage einstellen',
          ctaTo: '/l/new',
          status: s,
        }
      : {
          headline: `${s.label} läuft`,
          // Der erste Satz hiess frueher "Seit dem 1. September laeuft die
          // Hauptperiode - noch 20 Wochen bis zum 31. Januar". Genau das
          // zeigt der Zeitstrahl daneben inzwischen besser, als ein Satz es
          // koennte. Und "in fuenf Minuten eingestellt" steht auf dem Knopf.
          body: `Wer jetzt ausschreibt, trifft Händler und Hersteller in ihrer Planungsphase: Kapazitäten für die kommende Saison sind noch frei, und ein Liefertermin lässt sich leichter zusagen als mitten im Betrieb.`,
          ctaLabel: 'Anfrage einstellen',
          ctaTo: '/l/new',
          status: s,
        };
  }

  if (s.status === 'laufend' && !isClub) {
    return {
      headline: `${s.label} läuft — noch ${s.restLabel}`,
      body: `In dieser Periode kommt der Großteil der Ausschreibungen herein. Prüfen Sie regelmäßig die offenen Anfragen in Ihren Kategorien: Wer früh antwortet, ist im Vergleich der Anlage vorn dabei.`,
      ctaLabel: 'Offene Anfragen ansehen',
      ctaTo: '/s',
      status: s,
    };
  }

  if (s.status === 'beginnt-bald') {
    return isClub
      ? {
          headline: `In ${s.restLabelDativ} beginnt die ${s.label}`,
          body: `Am ${dateLabel(
            s.start
          )} geht es los. Anfragen, die vorher stehen, sind vom ersten Tag an sichtbar – und Sie sind durch, bevor die Anbieter ihre Kapazitäten für die Saison verplant haben.`,
          ctaLabel: 'Anfrage vorbereiten',
          ctaTo: '/l/new',
          status: s,
        }
      : {
          headline: `In ${s.restLabelDativ} beginnt die ${s.label}`,
          body: `Am ${dateLabel(
            s.start
          )} beginnt das nächste Beschaffungsfenster. Gute Gelegenheit, Kategorien, Liefergebiete und Ansprechpartner im Firmenprofil zu prüfen, damit Sie bei den passenden Anfragen auftauchen.`,
          ctaLabel: 'Firmenprofil prüfen',
          ctaTo: '/profile-settings',
          status: s,
        };
  }

  return isClub
    ? {
        headline: 'Zwischen den Beschaffungsperioden',
        body: `Die nächste Periode beginnt am ${dateLabel(
          s.start
        )}. Jetzt ist die ruhige Zeit, um Bedarf zu sammeln — Sie können Anfragen jederzeit einstellen, auch außerhalb der Perioden.`,
        ctaLabel: 'Anfrage einstellen',
        ctaTo: '/l/new',
        status: s,
      }
    : {
        headline: 'Zwischen den Beschaffungsperioden',
        body: `Die nächste Periode beginnt am ${dateLabel(
          s.start
        )}. Anfragen kommen auch jetzt herein, nur seltener. Gute Zeit, das Firmenprofil zu schärfen.`,
        ctaLabel: 'Offene Anfragen ansehen',
        ctaTo: '/s',
        status: s,
      };
};
